import { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  Avatar,
  Body,
  Button,
  Caption,
  DataTable,
  DropdownMenu,
  DropdownMenuItem,
  I,
  Pill,
  Row,
  SearchInput,
  SectionHead,
  Stack,
  type ColumnDef,
} from '@leanin/ui';
import { formatDateMedium } from '@/lib/format-date';
import {
  GROUP_INVITES_PAGE_SIZE,
  groupKeys,
  type MembershipRequestView,
} from '@/lib/queries/groups';
import {
  useResendMembershipInvite,
  useRevokeMembershipInvite,
} from '@/lib/mutations/groups';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { InlineStatus } from '../../InlineStatus';

// Outstanding pending invites for a circle/network, on the admin Members
// screen. The unified model issues two shapes from one `direction='invite'`
// row: OFF-platform (a magic-link email — `subject.email` set) and ON-platform
// (a person already on the platform awaiting acceptance — `subject.userId` set,
// `subject.email` null). Both must be visible and revocable here.
//
// Columnar table (Invitee / Invited as / Sent), server-paginated + searched —
// built to scale to thousands of invites per group, so a page only ever holds
// PAGE_SIZE rows in memory. The Invitee cell mirrors the members table's row:
// avatar + name with a title sublabel underneath.

type GroupKind = 'circle' | 'network';

const PAGE_SIZE = GROUP_INVITES_PAGE_SIZE;

// Invalidation prefix (inner node) — matches every page/search variant.
function invitesPrefix(kind: GroupKind, groupId: string) {
  return kind === 'circle'
    ? groupKeys.circle.invites(groupId)
    : groupKeys.network.invites(groupId);
}

function subjectName(
  subject: MembershipRequestView['subject'],
  fallback: string,
): string {
  return (
    `${subject.firstName ?? ''} ${subject.lastName ?? ''}`.trim() || fallback
  );
}

// Off-platform invites show their email; on-platform invites have none, so
// fall back to the invited member's name.
function inviteeLabel(invite: MembershipRequestView, fallback: string): string {
  return invite.subject.email ?? subjectName(invite.subject, fallback);
}

type RevokeTarget = { readonly inviteId: string; readonly label: string };

export function GroupPendingInvitesCard({
  kind,
  groupId,
}: {
  readonly kind: GroupKind;
  readonly groupId: string;
}) {
  const t = useTranslations('Group');
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [pageIndex, setPageIndex] = useState(0);
  // Parallel queries, each gated by `kind` via `enabled` — `circle.invitesPage`
  // and `network.invitesPage` are structurally identical leaves differing only
  // by a `'circle' | 'network'` key discriminator, which TS can't unify at a
  // single `useQuery`. Only the active branch fetches.
  const pageParams = { page: pageIndex + 1, q: debouncedSearch };
  const circleQuery = useQuery({
    ...groupKeys.circle.invitesPage(groupId, pageParams),
    enabled: kind === 'circle',
  });
  const networkQuery = useQuery({
    ...groupKeys.network.invitesPage(groupId, pageParams),
    enabled: kind === 'network',
  });
  const invitesQuery = kind === 'circle' ? circleQuery : networkQuery;
  const revoke = useRevokeMembershipInvite();
  const resend = useResendMembershipInvite();
  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);
  // Resend produces no visible row change, so surface an explicit inline result.
  const [resendResult, setResendResult] = useState<{
    readonly tone: 'success' | 'destructive';
    readonly text: string;
  } | null>(null);

  const handleResend = (invite: MembershipRequestView) => {
    const label = inviteeLabel(invite, t('admin.pendingInvites.memberFallback'));
    setResendResult(null);
    resend.mutate(
      { groupId, reqId: invite.id, kind },
      {
        onSuccess: () =>
          setResendResult({
            tone: 'success',
            text: t('admin.pendingInvites.resendSuccess', { label }),
          }),
        onError: () =>
          setResendResult({
            tone: 'destructive',
            text: t('admin.pendingInvites.resendError', { label }),
          }),
      },
    );
  };

  const data = invitesQuery.data;
  // Clamp: revoking the last row of the last page (or a stale deep page after a
  // filter change) leaves pageIndex past the end — snap back to the last page.
  useEffect(() => {
    if (data === undefined) return;
    const pageCount = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
    if (pageIndex > pageCount - 1) setPageIndex(pageCount - 1);
  }, [data, pageIndex]);

  if (data === undefined) return null;

  const { items: invites, total: totalPending } = data;
  const hasAnyPending = totalPending > 0 || debouncedSearch.length > 0;

  const columns: ColumnDef<MembershipRequestView>[] = [
    {
      id: 'invitee',
      header: t('admin.pendingInvites.columnInvitee'),
      meta: { mobilePrimary: true },
      // Same row structure as the members table: avatar + name with a title
      // sublabel underneath. Off-platform (email) invites show the address
      // with an "Email invitation" sublabel.
      cell: ({ row }) => {
        const { subject } = row.original;
        const onPlatform = subject.userId !== null;
        const name = onPlatform
          ? subjectName(subject, t('admin.pendingInvites.memberFallback'))
          : (subject.email ?? t('admin.pendingInvites.inviteeFallback'));
        const sublabel = onPlatform
          ? [subject.jobTitle, subject.company].filter(Boolean).join(' · ')
          : t('admin.pendingInvites.emailInvitation');
        return (
          <Row gap="md" align="center" style={{ minWidth: 0 }}>
            <Avatar src={subject.photoUrl ?? undefined} name={name} size={36} />
            <Stack gap="xs" style={{ minWidth: 0, flex: 1 }}>
              <Body size="md" style={{ overflowWrap: 'anywhere' }}>
                {name}
              </Body>
              {sublabel.length > 0 ? <Caption>{sublabel}</Caption> : null}
            </Stack>
          </Row>
        );
      },
    },
    {
      id: 'role',
      header: t('admin.pendingInvites.columnRole'),
      meta: { width: 120 },
      cell: ({ row }) => (
        <Pill tone={row.original.roleOnAccept === 'leader' ? 'burgundy' : 'neutral'}>
          {row.original.roleOnAccept === 'leader'
            ? t('admin.pendingInvites.roleLeader')
            : t('admin.pendingInvites.roleMember')}
        </Pill>
      ),
    },
    {
      id: 'sent',
      header: t('admin.pendingInvites.columnSent'),
      meta: { width: 140, nowrap: true },
      cell: ({ row }) => (
        <Body size="sm" color="mute">
          {formatDateMedium(row.original.createdAt)}
        </Body>
      ),
    },
    {
      id: 'actions',
      header: '',
      meta: { width: 64, align: 'end', nowrap: true },
      cell: ({ row }) => {
        const label = inviteeLabel(
          row.original,
          t('admin.pendingInvites.memberFallback'),
        );
        return (
          <DropdownMenu
            align="end"
            trigger={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                icon={I.more}
                aria-label={t('admin.pendingInvites.actionsAria', { label })}
              />
            }
          >
            <DropdownMenuItem onSelect={() => handleResend(row.original)}>
              {t('admin.pendingInvites.resend')}
            </DropdownMenuItem>
            <DropdownMenuItem
              destructive
              onSelect={() =>
                setRevokeTarget({ inviteId: row.original.id, label })
              }
            >
              {t('admin.pendingInvites.revoke')}
            </DropdownMenuItem>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <SectionHead
          title={t('admin.pendingInvites.title', { count: totalPending })}
        />
        <Body size="sm" color="mute">
          {t('admin.pendingInvites.description')}
        </Body>
      </Stack>
      {resendResult !== null ? (
        <InlineStatus tone={resendResult.tone}>
          {resendResult.text}
        </InlineStatus>
      ) : null}
      {!hasAnyPending ? (
        <Body size="sm" color="mute">
          {t('admin.pendingInvites.empty')}
        </Body>
      ) : (
        <Stack gap="md">
          <SearchInput
            placeholder={t('admin.pendingInvites.searchPlaceholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPageIndex(0);
            }}
            style={{ maxWidth: 320 }}
          />
          <DataTable
            data={[...invites]}
            columns={columns}
            pagination={{
              pageSize: PAGE_SIZE,
              pageIndex,
              onPageChange: setPageIndex,
              rowCount: totalPending,
            }}
          >
            <DataTable.Header />
            <DataTable.Body
              emptyMessage={t('admin.pendingInvites.tableEmpty')}
            />
            <DataTable.Pagination />
          </DataTable>
        </Stack>
      )}

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(next) => {
          if (!next) setRevokeTarget(null);
        }}
        title={t('admin.pendingInvites.revokeDialogTitle')}
        description={
          revokeTarget !== null
            ? t('admin.pendingInvites.revokeDialogDescription', {
                label: revokeTarget.label,
              })
            : ''
        }
        actionLabel={t('admin.pendingInvites.revokeDialogConfirm')}
        cancelLabel={t('admin.pendingInvites.revokeDialogCancel')}
        destructive
        onAction={() => {
          if (revokeTarget !== null) {
            revoke.mutate(
              { groupId, reqId: revokeTarget.inviteId, kind },
              {
                onSettled: () => {
                  void qc.invalidateQueries({
                    queryKey: invitesPrefix(kind, groupId),
                  });
                },
              },
            );
            setRevokeTarget(null);
          }
        }}
      />
    </Stack>
  );
}
